import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { formatWithThousandSep, transformToExpenseFormat, formatDateToYYYY_MM_DD } from '../utils/utils'
import { useAuth } from '../hooks/useAuth'
import useTemplateFetcher from '../hooks/useTemplateFetcher'
import { AppPath } from '../enums/AppPath'
import { Expense } from '../interfaces/VehicleBase'
import { FormatProps, handleInputChangeFormat } from '../utils/handleInputChangeFormat'
import { Rule, validateFields } from '../utils/validateForm'
import NumeroALetras from '../utils/numeroAletras'
import Vehicle from '../interfaces/Vehicle'
import Client from '../interfaces/Client'
import PurchaseDto from '../interfaces/PurchaseDto'
import { ExpenseFormat } from '../interfaces/VehicleBase'
import usePurchaseCrea from '../hooks/usePurchaseCrea'
import Purchase from '../interfaces/Purchase'
import UserData from '../interfaces/UserData'
import { OrganizationBase } from '../interfaces/OrganizationBase'

import SettlementCost from './SettlementCost'
import PurchasePreview from './PurchasePreview'
import InputField from './InputField'
import InputTextArea from './InputTextArea'
import { Info, Alert } from './MessageInfo'

export interface PurchaseShort {
  _id?: string
  voidedAt?: string
  voidedCause?: string
  price: number
  price_format: string
  payment: string
  date: string
  notes: string
  total: number
  total_format: string
  totalInWords: string
  number: number
  body: {
    organization: OrganizationBase
    profile?: {
      user: {
        firstname: string
        lastname: string
        nin: string
        phone?: string
      }
      email: string
    }
  }
  voidedBy_user?: string
}

export interface PurchaseFormat {
  _id?: string
  voidedAt?: string
  voidedCause?: string
  voidedBy_user?: string
  price: number
  price_format: string
  payment: string
  date: string
  notes: string
  total: number
  total_format: string
  totalInWords: string
  number: number
  vehicle: string
  client: string
  organization: string
  profile: string
  expenses?: ExpenseFormat[]
  profile_name?: string
  user_phone?: string
  body: {
    organization: OrganizationBase
    profile?: {
      user: {
        firstname: string
        lastname: string
        nin: string
        phone?: string
      }
      email: string
    }
  }
}

const PurchaseEdit: React.FC<{
  vehicle: Vehicle
  client: Client
  purchase?: Purchase
  onRefresh?: () => void
  onCreate?: (id: string) => void
  startInEditMode?: boolean
}> = ({ vehicle, client, purchase, onRefresh, onCreate, startInEditMode = false }) => {
  const { userData } = useAuth()
  const [formData, setFormData] = useState<PurchaseFormat | null>(null)
  const [costs, setCosts] = useState<ExpenseFormat[]>([])
  const { fetchTemplate } = useTemplateFetcher()
  const { errorPurchaseCrea, purchaseCrea } = usePurchaseCrea()
  // Errores de validacion de los input
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  // Not using modificandoSw but keeping setModificandoSw for potential future use
  const [, setModificandoSw] = useState<boolean>(startInEditMode)
  // Track submission state to hide button after click
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const organizationId = userData?.organization?._id ?? ''
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [infoMessage, setInfoMessage] = useState('')
  const navigate = useNavigate()

  // Add debug logging
  useEffect(() => {
    console.log('PurchaseEdit - Component mounted with:', {
      vehicle: vehicle ? `${vehicle._id}` : 'none',
      client: client ? `${client._id}` : 'none',
      purchase: purchase ? `ID: ${purchase._id}` : 'new purchase',
      startInEditMode,
    })
  }, [])

  useEffect(() => {
    console.log('PurchaseEdit initialized with:', {
      purchase: purchase ? 'existing purchase' : 'new purchase',
      startInEditMode,
    })

    const fetchDataAsync = async () => {
      try {
        // If we have a purchase, we're in modification mode
        if (purchase) {
          console.log('Setting up for existing purchase:', purchase._id)
          setFormData(purchaseInitial(userData, vehicle, client, purchase))

          // Transform expenses and ensure they're all marked as excluded (unchecked) in edit mode
          const transformedExpenses = transformToExpenseFormat(purchase.body.vehicle.expenses ?? [])
          const expensesWithExcluded = transformedExpenses.map((cost) => ({
            ...cost,
            excluded: true, // Set all costs as excluded by default in edit mode too
          }))

          setCosts(expensesWithExcluded)

          return
        }

        // For new purchases
        console.log('Setting up for new purchase with organizationId:', organizationId)
        const template = organizationId ? await fetchTemplate(organizationId, AppPath.Settlements) : null

        console.log('Template fetched:', template ? 'yes' : 'no')

        if (template) {
          const initialPurchase = purchaseInitial(userData, vehicle, client, null)
          const initialCosts = transformToExpenseFormat(vehicle.expenses ?? [])

          // Mark all costs as excluded (unchecked) by default when creating a new purchase
          const initialCostsWithExcluded = initialCosts.map((cost) => ({
            ...cost,
            excluded: true, // Set all costs as excluded by default in create mode
          }))

          setFormData(initialPurchase)
          setCosts(initialCostsWithExcluded)

          // Update total based on costs (which will all be excluded initially)
          const total = calcTotal(initialPurchase.price, initialCostsWithExcluded)
          const totalInWords = NumeroALetras(total)

          setFormData((prev) => {
            if (!prev) return null

            return {
              ...prev,
              total: total,
              total_format: formatWithThousandSep(total),
              totalInWords: totalInWords,
            }
          })
        } else {
          console.error('No template found for organizationId:', organizationId)
          setAlertMessage('Error: No se pudo cargar la plantilla de compra')
        }
      } catch (error) {
        console.error('Error in fetchDataAsync:', error)
        setAlertMessage('Error al cargar los datos de compra')
      }
    }
    fetchDataAsync()
  }, [organizationId, purchase])

  useEffect(() => {
    if (errorPurchaseCrea) {
      setAlertMessage(errorPurchaseCrea.message)
    }
  }, [errorPurchaseCrea])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    const formatProps: FormatProps[] = [{ name: 'price', name_format: 'price_format' }]
    handleInputChangeFormat<PurchaseFormat>(e, formData, setFormData, formatProps)

    if (name !== 'price_format') return

    let numericValue = value.replace(/[^\d]/g, '') // Strip non-numeric characters

    if (!numericValue) {
      numericValue = '0'
    }
    const num = Number(numericValue)
    const total = calcTotal(num, costs)
    const totalInWords = NumeroALetras(total)
    setFormData((prevFormData) => {
      if (!prevFormData) return null // To satisfy TypeScript null-checks

      return {
        ...prevFormData,
        total: total,
        total_format: formatWithThousandSep(total),
        totalInWords: totalInWords,
      }
    })
  }

  const handleCostsChange = (updatedCosts: typeof costs) => {
    setCosts(updatedCosts)

    if (formData !== null) {
      const total = calcTotal(formData?.price, updatedCosts)
      const totalInWords = NumeroALetras(total)
      setFormData((prevFormData) => {
        if (!prevFormData) {
          return null // To satisfy TypeScript null-checks
        }

        return {
          ...prevFormData,
          total: total,
          total_format: formatWithThousandSep(total),
          totalInWords: totalInWords,
        }
      })
    }
  }

  const calcTotal = (price: number, costs: ExpenseFormat[]): number => {
    const sumaNoExc = costs.reduce((sum, current) => (!current.excluded ? sum + Number(current?.cost ?? 0) : sum), 0)

    return Number(price ?? 0) - Number(sumaNoExc)
  }

  const vistaPreviaClick = () => {
    // Validate fields before allowing preview
    if (formData) {
      const fieldsToValidate = [
        { name: 'price_format', value: formData.price_format, rule: Rule.GREATER_ZERO },
        { name: 'date', value: formData.date, rule: Rule.REQUIRED },
        { name: 'payment', value: formData.payment, rule: Rule.REQUIRED },
        { name: 'totalInWords', value: formData.totalInWords, rule: Rule.REQUIRED },
      ]

      const validationErrors = validateFields(fieldsToValidate)

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors)
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' })

        return
      }
      setErrors({})
    }

    setShowPreview(true)
  }

  const handleGoBack = () => {
    // Navigate to the purchase list page using the correct route
    if (showPreview) {
      // Just go back to edit mode if we're in preview mode
      setShowPreview(false)
    } else {
      // Navigate to the purchase list if we're in edit mode
      navigate(`/${AppPath.Purchases}`)
    }
  }

  const handleSubmit = async () => {
    if (!formData) return

    // Set isSubmitting to true to hide the button immediately
    setIsSubmitting(true)

    // For new purchases when not in preview mode, validate fields
    if (!formData._id && !showPreview) {
      const fieldsToValidate = [
        { name: 'price_format', value: formData.price_format, rule: Rule.GREATER_ZERO },
        { name: 'date', value: formData.date, rule: Rule.REQUIRED },
        { name: 'payment', value: formData.payment, rule: Rule.REQUIRED },
        { name: 'totalInWords', value: formData.totalInWords, rule: Rule.REQUIRED },
      ]

      const validationErrors = validateFields(fieldsToValidate)

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors)
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' })
        // Reset submitting state if validation fails
        setIsSubmitting(false)

        return
      }
      setErrors({})
    }

    // For edits, must be in preview mode to save
    if (formData._id && !showPreview) {
      setAlertMessage('Para guardar los cambios, primero debes revisar en Vista Previa')
      // Reset submitting state if validation fails
      setIsSubmitting(false)

      return
    }

    // Validate MongoDB ObjectIDs before submitting
    const isValidObjectId = (id: string | undefined) => {
      return id && /^[0-9a-fA-F]{24}$/.test(id)
    }

    // Check that all required IDs are valid MongoDB ObjectIDs
    if (formData._id && !isValidObjectId(formData._id)) {
      setAlertMessage('Error: ID de compra inválido')

      return
    }

    if (!isValidObjectId(formData.vehicle)) {
      setAlertMessage('Error: ID de vehículo inválido')

      return
    }

    if (!isValidObjectId(formData.client)) {
      setAlertMessage('Error: ID de cliente inválido')

      return
    }

    if (!isValidObjectId(formData.organization)) {
      setAlertMessage('Error: ID de organización inválido')

      return
    }

    if (!isValidObjectId(formData.profile)) {
      setAlertMessage('Error: ID de perfil inválido')

      return
    }

    const expenses: Expense[] = []
    costs.forEach((cost) => {
      // Check if this is a newly added expense (has 'new_' prefix)
      const isNewExpense = cost._id && cost._id.toString().startsWith('new_')

      // Skip expenses with invalid IDs that aren't new expenses
      if (cost._id && !isNewExpense && !isValidObjectId(cost._id)) {
        console.warn(`Skipping expense with invalid ID: ${cost._id}`)

        return
      }

      const expense: Expense = {
        // For new expenses, don't include the ID so MongoDB will generate one
        _id: isNewExpense ? undefined : cost._id,
        cost: cost.cost,
        date: cost.date,
        description: cost.description,
        excluded: cost.excluded,
      }

      console.log('Adding expense to submission:', {
        description: expense.description,
        cost: expense.cost,
        isNew: isNewExpense,
      })

      expenses.push(expense)
    })

    // Check if this is a create or update operation
    const isUpdate = !!formData._id
    console.log(`Preparing to ${isUpdate ? 'update' : 'create'} purchase`, {
      id: formData._id || 'new',
      number: formData.number,
    })

    // Create PurchaseDto for submission
    const purchaseDto: PurchaseDto = {
      _id: formData._id, // Include ID for updates
      price: formData.price,
      notes: formData.notes,
      vehicle: formData.vehicle,
      client: formData.client,
      organization: formData.organization,
      profile: formData.profile,
      totalInWords: formData.totalInWords,
      payment: formData.payment,
      date: formData.date,
      number: isUpdate ? formData.number : 0, // Set to 0 for new purchases, keep number for updates
      expenses: expenses,
    }

    try {
      console.log('Submitting purchase with data:', {
        id: purchaseDto._id || 'new',
        number: purchaseDto.number,
        isUpdate: isUpdate,
        expensesCount: purchaseDto.expenses.length,
        expenses: purchaseDto.expenses.map((e) => ({
          id: e._id || 'new',
          description: e.description,
          cost: e.cost,
          excluded: e.excluded,
        })),
      })

      const purchaseId = await purchaseCrea(purchaseDto)

      if (purchaseId) {
        // Set success message
        const successMessage = formData._id ? 'Nota de Compra Modificada' : 'Compra creada exitosamente'
        setInfoMessage(successMessage)

        // If this is a new purchase and we're not in preview mode
        if (!formData._id && !showPreview) {
          if (onCreate) {
            onCreate(purchaseId)
          } else {
            navigate(`/${AppPath.Purchases}/view/${purchaseId}`)
          }
        } else if (!showPreview) {
          // For updates when not in preview mode
          if (onRefresh) {
            onRefresh()
          }
          setModificandoSw(false)
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Catch all errors
    } catch (error: any) {
      console.error('Error creating/updating purchase:', error)

      // Handle MongoDB ObjectID errors specifically
      if (error instanceof Error && error.message && error.message.includes('hex string')) {
        setAlertMessage('Error: Formato de ID inválido. Por favor, actualice la página e intente nuevamente.')
      } else if (error.response && error.response.data && error.response.data.message) {
        // Handle API error responses with messages
        setAlertMessage(`Error: ${error.response.data.message}`)
      } else {
        // Generic error message
        setAlertMessage('Error al guardar la compra. Intente nuevamente.')
      }

      // Even in preview mode, show error message
      if (showPreview) {
        // Exit preview mode to show the error
        setShowPreview(false)
      }
    }
  }

  const onChangeExcluded = (cost: number, excluded: boolean) => {
    const updatedCosts = [...costs]

    if (cost >= 0 && cost < updatedCosts.length) {
      updatedCosts[cost] = { ...updatedCosts[cost], excluded }
      handleCostsChange(updatedCosts)
    }
  }

  const onInfoTimeout = (): void => {
    setInfoMessage('')
  }

  const onAlertTimeout = (): void => {
    setAlertMessage('')
  }

  if (!formData) {
    return (
      <div className="p-4">
        <p>Cargando datos...</p>
      </div>
    )
  }

  return (
    <div className="mb-8">
      {alertMessage && <Alert message={alertMessage} trigger={Date.now()} onTimeout={onAlertTimeout} />}
      {infoMessage && <Info message={infoMessage} trigger={Date.now()} onTimeout={onInfoTimeout} />}

      {!showPreview ? (
        <div className="p-4">
          {/* Main Form - Match SaleNote's grid layout exactly */}
          <div>
            {/* Document Number, Price, Date in a grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
              {/* Document Number */}
              {formData._id ? (
                <DisplayValueVert label="Número" name="number" value={formData.number} />
              ) : (
                <div>
                  <InputField
                    label="Número"
                    name="number"
                    value={formData.number}
                    type="number"
                    handleInputChange={handleInputChange}
                  />
                  <div className="text-sm">Deja en cero para numeración automática</div>
                </div>
              )}

              {/* Purchase Price */}
              <InputField
                label="Precio Compra"
                type="text"
                name="price_format"
                value={formData.price_format}
                handleInputChange={handleInputChange}
                errors={errors}
              />

              {/* Date */}
              <InputField
                label="Fecha"
                type="date"
                name="date"
                value={formData.date}
                handleInputChange={handleInputChange}
                errors={errors}
              />

              {/* Total - Right aligned */}
              <div className="lg:col-span-2 text-right">
                <DisplayValueVertRes label="Total a pagar" value={formData.total_format} />
              </div>
            </div>

            {/* Costs section - With shadow and border like SaleNote */}
            <div className="border shadow-lg rounded-md p-4 mb-5">
              <div className="mb-3">
                <h3 className="text-lg font-semibold">Costos</h3>
              </div>
              <SettlementCost
                title="Gastos"
                array={costs}
                onCostsChange={handleCostsChange}
                showExcluded={true}
                onChangeExcluded={onChangeExcluded}
              />
            </div>

            {/* Payment Method and Total in Words */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div>
                <InputField
                  label="Medio de pago"
                  type="text"
                  name="payment"
                  value={formData.payment}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
              </div>

              <div className="flex flex-col">
                <label className="font-semibold mb-1">Total en palabras</label>
                <div className="border rounded p-2 text-sm text-gray-700">{formData.totalInWords}</div>
              </div>
            </div>

            {/* Notes */}
            <div className="mb-6">
              <InputTextArea
                label="Observaciones"
                name="notes"
                value={formData.notes}
                handleInputChange={handleInputChange}
                errors={errors}
                rows={4}
              />
            </div>

            {/* Vista Previa button centered */}
            <div className="flex justify-center mt-4">
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded"
                onClick={vistaPreviaClick}
              >
                Vista Previa
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Preview mode content
        <>
          {formData && (
            <PurchasePreview
              vehicle={vehicle}
              client={client}
              purchase={formData}
              costs={costs}
              consultaSw={true}
              onGoBack={handleGoBack}
              onCrearClick={handleSubmit}
              infoMessage={infoMessage}
              onInfoTimeout={onInfoTimeout}
              modificandoSw={!!formData._id}
              creandoSw={!formData._id}
              isSubmitting={isSubmitting}
            />
          )}
        </>
      )}
    </div>
  )
}

// Helper function to initialize purchase data
const purchaseInitial = (
  userData: UserData | null,
  vehicleObj: Vehicle,
  clientObj: Client,
  purchase?: Purchase | null
): PurchaseFormat => {
  const today = new Date()
  const today_format = formatDateToYYYY_MM_DD(today)

  // For edit mode - use existing purchase data
  if (purchase) {
    return {
      _id: purchase._id,
      number: purchase.number ?? 0,
      price: purchase.price,
      price_format: formatWithThousandSep(purchase.price),
      payment: purchase.payment,
      date: purchase.date,
      notes: purchase.notes,
      total: purchase.total,
      total_format: formatWithThousandSep(purchase.total),
      totalInWords: purchase.totalInWords,
      body: {
        organization: purchase.body.organization,
        profile: purchase.body.profile,
      },
      voidedAt: purchase.voidedAt,
      voidedCause: purchase.voidedCause,
      voidedBy_user: purchase.voidedBy,
      vehicle: purchase.vehicle,
      client: purchase.client,
      organization: purchase.organization,
      profile: purchase.profile,
      profile_name: `${purchase.body.profile.user.firstname} ${purchase.body.profile.user.lastname}`,
      user_phone: purchase.body.profile.user.phone ?? '',
    }
  }

  // For new purchase - initialize with defaults
  return {
    number: 0, // Server will assign a number
    price: 0,
    price_format: '0',
    payment: '',
    date: today_format,
    notes: '',
    total: 0,
    total_format: '0',
    totalInWords: '',
    body: {
      organization: {
        name: userData?.organization?.name ?? '',
        address: userData?.organization?.address ?? '',
        phone: userData?.organization?.phone ?? '',
        email: userData?.organization?.email ?? '',
        // Required properties for OrganizationBase/OrganizationDto
        pictures: {
          logotype: userData?.organization?.pictures?.logotype ?? '',
          isotype: userData?.organization?.pictures?.isotype ?? '',
        },
        representatives: userData?.organization?.representatives ?? '',
        streetNumber: userData?.organization?.streetNumber ?? 0,
        description: userData?.organization?.description ?? '',
        legalName: userData?.organization?.legalName ?? '',
        business: userData?.organization?.business ?? '',
        domain: userData?.organization?.domain ?? '',
        nin: userData?.organization?.nin ?? '',
      },
    },
    vehicle: vehicleObj._id,
    client: clientObj._id,
    organization: userData?.organization?._id ?? '',
    profile: userData?.profile?._id ?? '',
    profile_name: userData?.user?.fullname ?? '',
    user_phone: '',
  }
}

// Helper components to match SaleNote styles
export const DisplayValueVert: React.FC<{ label: string; name: string; value?: string | number }> = ({
  label,
  value,
}) => {
  return (
    <div className="flex flex-col">
      <label className="font-semibold mb-1">{label}</label>
      <div className="border rounded p-2 bg-gray-100">{value || 'Se asignará automáticamente'}</div>
    </div>
  )
}

export const DisplayValueVertRes: React.FC<{ label: string; value?: string }> = ({ label, value }) => {
  const myClassName = 'flex flex-col'
  const signo = value?.startsWith('-')
  const borderColor = signo ? 'ring-4 ring-red-500' : ''

  return (
    <div className={myClassName}>
      <label className="font-semibold mb-1 text-right">{label}</label>
      <label className={`border rounded p-2 ${borderColor} font-bold`}>{value}</label>
    </div>
  )
}

export default PurchaseEdit
